// Must be imported first to ensure tailwind layer order is defined before other styles
import "./index.css";

import { useContext, useMemo } from "react";
import { StatsigProvider, useGateValue } from "@statsig/react-bindings";
import { ReactQueryDevtools } from "@tanstack/react-query-devtools";
import { OpsStats } from "express-server/metrics";
import { FormattedMessage } from "react-intl";
import {
  data,
  isRouteErrorResponse,
  Link,
  Links,
  Meta,
  Outlet,
  redirect,
  Scripts,
  ScrollRestoration,
  useLocation,
  useRevalidator,
  useRouteLoaderData,
} from "react-router";
import { getUserHasSofaAccess } from "src/lib/getUserHasSofaAccess";
import { NonceContext } from "src/lib/nonce";
import { Button } from "@oai/platform/ui/Button";
import { PlatformUIKitProvider } from "@oai/platform/ui/PlatformUIKitProvider";

import { AdsMeasurementPixel } from "@/components/common/AdsMeasurementPixel";
import { CookieBanner } from "@/components/common/CookieBanner";
import { CookiePreferences } from "@/components/common/CookiePreferences";
import { GoogleAdsTag } from "@/components/common/GoogleAdsTag";
import { LinkedInAdsTag } from "@/components/common/LinkedInAdsTag";
import { MetaAdsTag } from "@/components/common/MetaAdsTag";
import { RedditAdsTag } from "@/components/common/RedditAdsTag";
import RouterNavigationProgressBar from "@/components/common/RouterNavigationProgressBar";
import { CookieConsentProvider } from "@/hooks/useCookieConsent";
import { CookieLocationProvider } from "@/hooks/useCookieLocation";
import { GlobalContextProvider } from "@/hooks/useGlobalContext";
import { StatsigBootstrapReadinessContext, useStatsigClient } from "@/hooks/useStatsigClient";
import { useStatsigSessionReplayClient } from "@/hooks/useStatsigSessionReplayClient";
import { IntlProvider } from "@/IntlProvider";
import {
  buildAdAccountAccessChangedRecoveryPath,
  buildPathWithAdAccount,
  getAdAccountIdFromSearchParams,
  isAdAccountAccessChangedRecoveryPath,
  normalizeRoutePathname,
} from "@/lib/adAccountIdUtils";
import { isAdPreviewPath } from "@/lib/adPreview";
import {
  ClientFirstPaintLogger,
  ClientRouteLoadLogger,
  ClientTelemetryProvider,
} from "@/lib/clientTelemetry";
import { getCookieFromHeader, ONBOARDING_CREATED_AD_ACCOUNT_ID_COOKIE } from "@/lib/cookie";
import {
  COOKIE_PERSONALIZATION_CHOICES_ENABLED_GATE,
  getCookieBannerDismissedFromHeader,
  getCookieConsentFromHeader,
  getCookieConsentSourceFromHeader,
  getDevCookieCountryOverride,
  getDevCookieRegionOverride,
} from "@/lib/cookieConsent";
import {
  appendGoogleAdsClickIdToPath,
  getGoogleAdsClickId,
  GOOGLE_ADS_CLICK_ID_COOKIE_MAX_AGE_DAYS,
  GOOGLE_ADS_CLICK_ID_QUERY_PARAMS,
  GOOGLE_ADS_CLICK_ID_STORAGE_KEYS,
  isGoogleAdsServerAttributionEligible,
  isGoogleAdsUrlAttributionEligible,
  removeGoogleAdsClickIdsFromSearchParams,
} from "@/lib/googleAdsClickId";
import {
  appendLinkedInAdsClickIdToPath,
  getLinkedInAdsClickId,
  isLinkedInAdsServerAttributionEligible,
  LINKEDIN_ADS_CLICK_ID_QUERY_PARAM,
} from "@/lib/linkedInAdsClickId";
import {
  appendMetaAdsClickIdToPath,
  getMetaAdsClickId,
  isMetaAdsRolloutCountryHeader,
  META_ADS_CLICK_ID_QUERY_PARAM,
} from "@/lib/metaAdsClickId";
import {
  buildOnboardingPath,
  CREATE_NEW_AD_ACCOUNT_QUERY_PARAM,
  hasCreateNewAdAccountIntent,
  PROSPECT_CAMPAIGN_TOKEN_QUERY_PARAM,
  shouldShowPostVerificationHoldingScreen,
  shouldShowVerificationKickoff,
} from "@/lib/onboardingRouting";
import {
  ACCOUNT_SETUP_DEEPLINK_QUERY_PARAM,
  buildOnboardingAccountSetupPath,
  buildOnboardingCampaignSetupPath,
  getOnboardingAccountSetupStep,
  hasDismissedOnboardingCampaignSetup,
  isOnboardingAccountSetupFlow,
  ONBOARDING_ACCOUNT_SETUP_FLOW_QUERY_VALUE,
  ONBOARDING_CAMPAIGN_SETUP_PATH,
  ONBOARDING_SETUP_FLOW_QUERY_PARAM,
  ONBOARDING_SETUP_STEP_QUERY_PARAM,
} from "@/lib/onboardingSetupFlow";
import { isPublicPath } from "@/lib/publicRoutes";
import {
  appendRedditAdsClickIdToPath,
  getRedditAdsClickId,
  isRedditAdsServerAttributionEligible,
  REDDIT_ADS_CLICK_ID_QUERY_PARAM,
} from "@/lib/redditAdsClickId";
import { StaleNavigationProvider } from "@/lib/staleNavigation";
import { getThemeFromCookieHeader } from "@/lib/theme";
import { AppToaster } from "@/lib/toaster";
import {
  DEFAULT_LOCALE,
  getLocaleDirection,
  getLocaleFromLandingPathname,
  importLocaleJson,
  LOCALE_SEARCH_PARAM,
} from "@/locale";
import { getLocaleForLandingRequest } from "@/locale.server";
import { messages as rootMessages } from "@/localization/Root.messages";
import { isEditCampaignDeeplinkUrl } from "@/routes/adManager/editCampaignDeeplink";

import type { SofaAppLoadContext } from "express-server/main";
import type { GlobalContext } from "@/hooks/useGlobalContext";
import type { GoogleAdsClickId } from "@/lib/googleAdsClickId";
import type { StatsigBootstrapData } from "@/lib/statsigBootstrap";
import type { Route } from "./+types/root";

declare global {
  interface PlatformUIKitOverrides {
    LinkComponent: typeof Link;
  }
}

export type RootLoaderData = Route.ComponentProps["loaderData"];
const ONBOARDING_PAUSED_UNAVAILABLE_PATH = "/unavailable?reason=onboarding_paused";
const OPPREF_QUERY_PARAM = "oppref";
const ADVERTISER_IDENTITY_VERIFICATION_DEEPLINK_QUERY_PARAM = "verify_business";
const EMPTY_STATSIG_BOOTSTRAP: StatsigBootstrapData = {
  user: {},
  initializeValues: "{}",
};

type CookieConsentBootstrapProps = Pick<
  RootLoaderData,
  | "initialAnalyticsConsent"
  | "initialMarketingConsent"
  | "initialPersonalizationConsent"
  | "initialAnalyticsConsentSource"
  | "initialMarketingConsentSource"
  | "initialPersonalizationConsentSource"
  | "initialCookieBannerDismissed"
>;

export function Layout({ children }: { children: React.ReactNode }) {
  const data = useRouteLoaderData<RootLoaderData>("root");
  const nonce = useContext(NonceContext);
  const locale = data?.locale ?? DEFAULT_LOCALE;
  const location = useLocation();
  const isPreviewRoute = isAdPreviewPath(location.pathname);
  const googleAdsClickIdCaptureScript =
    !isPreviewRoute && data?.initialGoogleAdsClickIdCaptureEnabled ?
      buildGoogleAdsClickIdCaptureScript()
    : undefined;
  return (
    <html data-theme={data?.theme} lang={locale} dir={getLocaleDirection(locale)}>
      <head>
        <meta charSet="utf-8" />
        {googleAdsClickIdCaptureScript ?
          <script
            nonce={nonce}
            dangerouslySetInnerHTML={{ __html: googleAdsClickIdCaptureScript }}
          />
        : null}

        <link rel="icon" type="image/png" href="/favicon.png" />
        <link rel="apple-touch-icon" sizes="180x180" href="/apple-touch-icon.png" />
        <link rel="preconnect" href="https://cdn.openai.com" />
        {/* Preload critical fonts */}
        <link
          rel="preload"
          href="https://cdn.openai.com/common/fonts/openai-sans/v2/OpenAISans-Regular.woff2"
          as="font"
          type="font/woff2"
          crossOrigin="anonymous"
        />
        <link
          rel="preload"
          href="https://cdn.openai.com/common/fonts/openai-sans/v2/OpenAISans-Medium.woff2"
          as="font"
          type="font/woff2"
          crossOrigin="anonymous"
        />
        <link
          rel="preload"
          href="https://cdn.openai.com/common/fonts/openai-sans/v2/OpenAISans-Semibold.woff2"
          as="font"
          type="font/woff2"
          crossOrigin="anonymous"
        />
        <meta name="viewport" content="width=device-width, initial-scale=1" />
        <meta name="theme-color" content="#000000" />
        <Meta />
        <Links />
      </head>
      <body>
        {children} {/* Rendered route content goes here */}
        <ScrollRestoration nonce={nonce} />
        <Scripts nonce={nonce} /> {/* React hydration scripts */}
      </body>
    </html>
  );
}

function buildGoogleAdsClickIdCaptureScript(): string {
  return `
(() => {
  try {
    if (navigator.globalPrivacyControl === true) return;

    const url = new URL(window.location.href);
    const queryParams = ${JSON.stringify(GOOGLE_ADS_CLICK_ID_QUERY_PARAMS)};
    let clickIdType;
    let clickId;
    for (const queryParam of queryParams) {
      const values = url.searchParams.getAll(queryParam);
      if (values.length === 0) continue;
      if (values.length !== 1) return;
      clickIdType = queryParam;
      clickId = values[0];
      break;
    }
    if (!clickIdType) return;

    if (
      !clickId ||
      clickId.trim() !== clickId ||
      clickId.length > 256 ||
      /[\\u0000-\\u001F\\u007F]/.test(clickId)
    ) {
      return;
    }

    try {
      const storageKeys = ${JSON.stringify(GOOGLE_ADS_CLICK_ID_STORAGE_KEYS)};
      for (const storageKey of Object.values(storageKeys)) {
        window.sessionStorage.removeItem(storageKey);
      }
      window.sessionStorage.setItem(storageKeys[clickIdType], clickId);
    } catch {}

    try {
      const secureAttribute = window.location.protocol === "https:" ? "; Secure" : "";
      for (const queryParam of queryParams) {
        document.cookie = queryParam + "=; Path=/; Max-Age=0; SameSite=Lax" + secureAttribute;
      }
      document.cookie =
        clickIdType +
        "=" +
        encodeURIComponent(clickId) +
        "; Path=/; Max-Age=${GOOGLE_ADS_CLICK_ID_COOKIE_MAX_AGE_DAYS * 24 * 60 * 60}; SameSite=Lax" +
        secureAttribute;
    } catch {}

    for (const queryParam of queryParams) {
      url.searchParams.delete(queryParam);
    }
    window.history.replaceState(window.history.state, "", url.pathname + url.search + url.hash);
  } catch {}
})();
`;
}

export const clientMiddleware: Route.ClientMiddlewareFunction[] = [
  async ({ url }, next) => {
    const results = await next();
    if (!url.hash) {
      return results;
    }

    for (const result of Object.values(results)) {
      if (!(result.result instanceof Response)) {
        continue;
      }

      const location = result.result.headers.get("Location");
      if (location && isAdAccountAccessChangedRecoveryPath(location)) {
        result.result.headers.set("Location", buildAdAccountAccessChangedRecoveryPath(url));
      }
    }

    return results;
  },
];

export async function loader({
  request,
  context,
}: Route.LoaderArgs & { context: SofaAppLoadContext }) {
  const url = new URL(request.url);
  const isPreviewRoute = isAdPreviewPath(url.pathname);
  const cookieHeader = isPreviewRoute ? "" : request.headers.get("cookie") ?? "";
  const hasEffectiveMarketingConsent =
    !isPreviewRoute && hasEffectiveSignedOutMarketingConsent(request);
  const countryHeader = request.headers.get("cf-ipcountry");
  const isGoogleAdsServerAttributionAllowed =
    !isPreviewRoute &&
    isGoogleAdsServerAttributionEligible({
      cookieHeader: cookieHeader || undefined,
      globalPrivacyControlHeader: request.headers.get("Sec-GPC"),
      countryHeader,
    });
  const googleAdsClickId = getGoogleAdsClickId(url.searchParams);
  const consentedGoogleAdsClickId =
    isGoogleAdsServerAttributionAllowed ? googleAdsClickId : undefined;
  const googleAdsUrlClickId =
    (
      isGoogleAdsUrlAttributionEligible({
        cookieHeader: cookieHeader || undefined,
        globalPrivacyControlHeader: request.headers.get("Sec-GPC"),
        countryHeader,
      })
    ) ?
      googleAdsClickId
    : undefined;
  const metaAdsClickId = getMetaAdsClickId(url.searchParams);
  const consentedMetaAdsClickId =
    hasEffectiveMarketingConsent && isMetaAdsRolloutCountryHeader(countryHeader) ? metaAdsClickId
    : undefined;
  const linkedInAdsClickId = getLinkedInAdsClickId(url.searchParams);
  const consentedLinkedInAdsClickId =
    (
      isLinkedInAdsServerAttributionEligible({
        cookieHeader: cookieHeader || undefined,
        globalPrivacyControlHeader: request.headers.get("Sec-GPC"),
        countryHeader,
      })
    ) ?
      linkedInAdsClickId
    : undefined;
  const redditAdsClickId = getRedditAdsClickId(url.searchParams);
  const consentedRedditAdsClickId =
    (
      isRedditAdsServerAttributionEligible({
        cookieHeader: cookieHeader || undefined,
        globalPrivacyControlHeader: request.headers.get("Sec-GPC"),
        countryHeader,
      })
    ) ?
      redditAdsClickId
    : undefined;
  const redirectWithAdsClickIds = (path: string) =>
    redirect(
      appendRedditAdsClickIdToPath(
        appendLinkedInAdsClickIdToPath(
          appendMetaAdsClickIdToPath(
            appendGoogleAdsClickIdToPath(path, googleAdsUrlClickId),
            consentedMetaAdsClickId,
          ),
          consentedLinkedInAdsClickId,
        ),
        consentedRedditAdsClickId,
      ),
    );

  if (!isPreviewRoute && context.shouldRecoverFromSelectedAccount) {
    return redirectWithAdsClickIds(buildAdAccountAccessChangedRecoveryPath(request.url));
  }

  if (!isPreviewRoute && context.didEncounterAuthError) {
    if (context.isAuthInvalid) {
      return redirect("/auth/logout");
    }
    throw data(null, {
      status: 503,
      statusText: "Service temporarily unavailable",
    });
  }

  const salesLedOnboardingPrefillId = getSalesLedOnboardingPrefillId(url.searchParams);
  const oppref = getOppref(url.searchParams);
  const prospectCampaignTokenFromPath = getProspectCampaignTokenFromPath(url.pathname);
  if (prospectCampaignTokenFromPath != null) {
    return redirect(
      buildOnboardingPath({
        prefillId: null,
        oppref: null,
        prospectCampaignToken: prospectCampaignTokenFromPath,
      }),
    );
  }
  const prospectCampaignToken =
    url.searchParams.get(PROSPECT_CAMPAIGN_TOKEN_QUERY_PARAM)?.trim() || null;

  const isLoggedIn = !!context.user;
  const isPublicRoute = isPublicPath(url.pathname);
  const isNoAdAccountRoute = isNoAdAccountPath(url.pathname);
  const isOnboardingCampaignSetupRoute = isOnboardingCampaignSetupPath(url.pathname);
  const isOnboardingAccountSetupRequest = isOnboardingAccountSetupFlow(url.searchParams);
  const isOnboardingAccountSetupRoute =
    isOnboardingCampaignSetupRoute &&
    isOnboardingAccountSetupRequest &&
    context.isMobileWebAccountSetupEnabled === true;
  const isPendingInvitationsRoute = isPendingInvitationsPath(url.pathname);
  const isSalesLedOnboardingRoute = salesLedOnboardingPrefillId != null && isNoAdAccountRoute;
  const isCreateNewAdAccountRoute =
    isNoAdAccountRoute && hasCreateNewAdAccountIntent(url.searchParams);
  const requiresAuth = !isPublicRoute;
  if (!isLoggedIn && requiresAuth) {
    // Page needs auth but user doesn't have it
    return redirect(
      buildLoginPath({
        returnTo: getLoginReturnToPath({
          url,
          salesLedOnboardingPrefillId,
          prospectCampaignToken,
          googleAdsClickId: consentedGoogleAdsClickId,
          metaAdsClickId: consentedMetaAdsClickId,
          linkedInAdsClickId: consentedLinkedInAdsClickId,
          redditAdsClickId: consentedRedditAdsClickId,
        }),
      }),
    );
  }
  if (isLoggedIn && requiresAuth) {
    // Page needs auth and user has it
    if (
      isOnboardingAccountSetupRequest &&
      context.isMobileWebAccountSetupEnabled !== true &&
      (isNoAdAccountRoute || isOnboardingCampaignSetupRoute)
    ) {
      url.searchParams.delete(ONBOARDING_SETUP_FLOW_QUERY_PARAM);
      url.searchParams.delete(ONBOARDING_SETUP_STEP_QUERY_PARAM);
      return redirectWithAdsClickIds(`${url.pathname}${url.search}${url.hash}`);
    }
    if (
      isPendingInvitationsRoute &&
      hasCreateNewAdAccountIntent(url.searchParams) &&
      context.isCreateNewAdAccountEnabled !== true
    ) {
      url.searchParams.delete(CREATE_NEW_AD_ACCOUNT_QUERY_PARAM);
      return redirectWithAdsClickIds(`${url.pathname}${url.search}${url.hash}`);
    }
    if (!getUserHasSofaAccess(context)) {
      // ... but no ads manager access / no accounts available
      OpsStats.incrementAccessBlocked({ context: "loader", reason: "no_ad_account" });
      if (
        isNoAdAccountRoute &&
        hasCreateNewAdAccountIntent(url.searchParams) &&
        context.isCreateNewAdAccountEnabled !== true
      ) {
        url.searchParams.delete(CREATE_NEW_AD_ACCOUNT_QUERY_PARAM);
        return redirectWithAdsClickIds(`${url.pathname}${url.search}${url.hash}`);
      }
      if (context.isSelfServeOnboardEnabled || salesLedOnboardingPrefillId) {
        // Begin onboarding flow to join or create account
        if (!isNoAdAccountRoute) {
          return redirectWithAdsClickIds(
            buildOnboardingPath({
              prefillId: salesLedOnboardingPrefillId,
              oppref,
              prospectCampaignToken,
            }),
          );
        }
      } else {
        return redirectWithAdsClickIds(ONBOARDING_PAUSED_UNAVAILABLE_PATH);
      }
    } else {
      if (salesLedOnboardingPrefillId && !isNoAdAccountRoute) {
        return redirectWithAdsClickIds(
          buildOnboardingPath({
            prefillId: salesLedOnboardingPrefillId,
            oppref,
            prospectCampaignToken,
          }),
        );
      }

      if (context.isSelfServeOnboardEnabled === false && isPendingInvitationsRoute) {
        OpsStats.incrementAccessBlocked({ context: "loader", reason: "feature_gate" });
        return redirectWithAdsClickIds(ONBOARDING_PAUSED_UNAVAILABLE_PATH);
      }

      const shouldStayOnVerificationKickoff =
        !isSalesLedOnboardingRoute && shouldShowVerificationKickoff(context);
      const shouldStayOnPostVerificationHoldingScreen =
        !isSalesLedOnboardingRoute && shouldShowPostVerificationHoldingScreen(context);
      const shouldStayOnOnboardingRoute =
        shouldStayOnVerificationKickoff || shouldStayOnPostVerificationHoldingScreen;
      const queryAdAccountId = getAdAccountIdFromSearchParams(url.searchParams);
      const recentlyCreatedOnboardingAdAccountId = getCookieFromHeader(
        cookieHeader,
        ONBOARDING_CREATED_AD_ACCOUNT_ID_COOKIE,
      );
      const shouldPreserveOnboardingQueryAdAccount =
        isNoAdAccountRoute &&
        queryAdAccountId != null &&
        queryAdAccountId !== context.adAccountId &&
        queryAdAccountId === recentlyCreatedOnboardingAdAccountId;
      const shouldStayOnCreateNewAdAccountRoute =
        isCreateNewAdAccountRoute && context.isCreateNewAdAccountEnabled === true;
      if (
        isCreateNewAdAccountRoute &&
        shouldPreserveOnboardingQueryAdAccount &&
        !shouldStayOnCreateNewAdAccountRoute
      ) {
        url.searchParams.delete(CREATE_NEW_AD_ACCOUNT_QUERY_PARAM);
        return redirectWithAdsClickIds(`${url.pathname}${url.search}${url.hash}`);
      }
      if (
        !shouldStayOnOnboardingRoute &&
        isOnboardingCampaignSetupRoute &&
        !isOnboardingAccountSetupRoute &&
        prospectCampaignToken == null &&
        context.shouldRunOnboardingCampaignSetup !== true
      ) {
        return redirectWithAdsClickIds(
          buildPathWithAdAccount("/manage/campaigns", context.adAccountId),
        );
      }
      if (shouldStayOnOnboardingRoute) {
        if (!isNoAdAccountRoute && !isPendingInvitationsRoute) {
          const onboardingUrl = new URL(
            buildPathWithAdAccount("/onboarding", context.adAccountId),
            url,
          );
          if (prospectCampaignToken != null) {
            onboardingUrl.searchParams.set(
              PROSPECT_CAMPAIGN_TOKEN_QUERY_PARAM,
              prospectCampaignToken,
            );
          }
          if (isOnboardingAccountSetupRoute) {
            onboardingUrl.searchParams.set(
              ONBOARDING_SETUP_FLOW_QUERY_PARAM,
              ONBOARDING_ACCOUNT_SETUP_FLOW_QUERY_VALUE,
            );
            const setupStep = getOnboardingAccountSetupStep(url.searchParams);
            if (setupStep) {
              onboardingUrl.searchParams.set(ONBOARDING_SETUP_STEP_QUERY_PARAM, setupStep);
            }
          }
          return redirectWithAdsClickIds(`${onboardingUrl.pathname}${onboardingUrl.search}`);
        }

        if (queryAdAccountId != context.adAccountId && !shouldPreserveOnboardingQueryAdAccount) {
          return redirectWithAdsClickIds(buildPathWithAdAccount(url, context.adAccountId));
        }
      } else if (isNoAdAccountRoute && !isSalesLedOnboardingRoute) {
        if (!shouldPreserveOnboardingQueryAdAccount && !shouldStayOnCreateNewAdAccountRoute) {
          if (isOnboardingAccountSetupRequest) {
            return redirectWithAdsClickIds(
              buildOnboardingAccountSetupPath(
                context.adAccountId,
                getOnboardingAccountSetupStep(url.searchParams),
                prospectCampaignToken,
              ),
            );
          }
          if (prospectCampaignToken != null) {
            return redirectWithAdsClickIds(
              buildOnboardingCampaignSetupPath(context.adAccountId, prospectCampaignToken),
            );
          }
          if (
            context.shouldRunOnboardingCampaignSetup &&
            !hasDismissedOnboardingCampaignSetup(cookieHeader, context.adAccountId)
          ) {
            return redirectWithAdsClickIds(
              buildOnboardingCampaignSetupPath(context.adAccountId, prospectCampaignToken),
            );
          }
          return redirectWithAdsClickIds(
            buildPathWithAdAccount("/manage/campaigns", context.adAccountId),
          );
        }
        if (
          shouldStayOnCreateNewAdAccountRoute &&
          context.adAccountId != null &&
          queryAdAccountId !== context.adAccountId &&
          !shouldPreserveOnboardingQueryAdAccount
        ) {
          return redirectWithAdsClickIds(buildPathWithAdAccount(url, context.adAccountId));
        }
      }
      if (
        !shouldStayOnOnboardingRoute &&
        !isNoAdAccountRoute &&
        queryAdAccountId != context.adAccountId
      ) {
        // ... but ad account in URL doesn't match user's ad account
        return redirectWithAdsClickIds(buildPathWithAdAccount(url, context.adAccountId));
      }
    }
  }

  OpsStats.increment("server_loader_success", {});
  const locale = getLocaleForLandingRequest(request, context.user?.locale ?? null);
  const messages = await importLocaleJson(locale);
  const theme = getThemeFromCookieHeader(cookieHeader);
  return data({
    user: context.user,
    adAccountId: context.adAccountId,
    adAccountStatus: context.adAccountStatus,
    adAccountCountryCode: context.adAccountCountryCode,
    adAccountCurrencyCode: context.adAccountCurrencyCode,
    adAccountTimezone: context.adAccountTimezone,
    adAccountUrl: context.adAccountUrl,
    adAccountIsSalesManaged: context.adAccountIsSalesManaged,
    adAccountAdvertiserManagementType: context.adAccountAdvertiserManagementType,
    adAccountHasAdsOnboardingIntent: context.adAccountHasAdsOnboardingIntent,
    adAccountBrandReviewStatus: context.adAccountBrandReviewStatus,
    adAccountBrandReviewReason: context.adAccountBrandReviewReason,
    adAccountIntegrityReviewStatus: context.adAccountIntegrityReviewStatus,
    adAccountNeedsPersonaVerification: context.adAccountNeedsPersonaVerification,
    adAccountNeedsPersonaVerificationStepUp: context.adAccountNeedsPersonaVerificationStepUp,
    adAccountPersonaVerificationStatus: context.adAccountPersonaVerificationStatus,
    adAccountShouldBeOnHoldingScreenPostVerification:
      context.adAccountShouldBeOnHoldingScreenPostVerification,
    adAccountAdsManagerAccessHoldReason: context.adAccountAdsManagerAccessHoldReason,
    adAccountOnboardingAvailabilityHoldReason: context.adAccountOnboardingAvailabilityHoldReason,
    adAccountIntegrityReviewFailureReason: context.adAccountIntegrityReviewFailureReason,
    adAccountPersonaVerificationApprovedBannerDismissed:
      context.adAccountPersonaVerificationApprovedBannerDismissed,
    adAccountPermissions: context.adAccountPermissions,
    canCreateNewAdAccountWithoutLimit: context.canCreateNewAdAccountWithoutLimit,
    isCreateNewAdAccountEnabled: context.isCreateNewAdAccountEnabled,
    isMobileWebAccountSetupEnabled: context.isMobileWebAccountSetupEnabled,
    shouldRunOnboardingCampaignSetup: context.shouldRunOnboardingCampaignSetup,
    adsInsightsV2ReadEnabled: context.adsInsightsV2ReadEnabled,
    statsigBootstrap:
      isPreviewRoute ?
        context.statsigBootstrap ?? EMPTY_STATSIG_BOOTSTRAP
      : context.statsigBootstrap,
    initialAnalyticsConsent: getCookieConsentFromHeader(cookieHeader, "analytics_consent"),
    initialMarketingConsent: getCookieConsentFromHeader(cookieHeader, "marketing_consent"),
    initialPersonalizationConsent: getCookieConsentFromHeader(
      cookieHeader,
      "personalization_consent",
    ),
    initialAnalyticsConsentSource: getCookieConsentSourceFromHeader(
      cookieHeader,
      "analytics_consent_source",
    ),
    initialMarketingConsentSource: getCookieConsentSourceFromHeader(
      cookieHeader,
      "marketing_consent_source",
    ),
    initialPersonalizationConsentSource: getCookieConsentSourceFromHeader(
      cookieHeader,
      "personalization_consent_source",
    ),
    initialCookieBannerDismissed: getCookieBannerDismissedFromHeader(cookieHeader),
    initialGoogleAdsClickIdCaptureEnabled: isGoogleAdsServerAttributionAllowed,
    adsMeasurementPixelId: isPreviewRoute ? "" : process.env.ADS_MEASUREMENT_PIXEL_ID?.trim() ?? "",
    adsMeasurementDebug: isPreviewRoute ? false : process.env.ADS_MEASUREMENT_DEBUG === "true",
    googleAdsTagId: isPreviewRoute ? "" : process.env.GOOGLE_ADS_TAG_ID?.trim() ?? "",
    googleAdsAccountCreationLabel:
      isPreviewRoute ? "" : process.env.GOOGLE_ADS_ACCOUNT_CREATION_CONVERSION_LABEL?.trim() ?? "",
    metaAdsPixelId: isPreviewRoute ? "" : process.env.META_ADS_PIXEL_ID?.trim() ?? "",
    linkedInAdsPartnerId: isPreviewRoute ? "" : process.env.LINKEDIN_ADS_PARTNER_ID?.trim() ?? "",
    linkedInAdsAccountCreationConversionId:
      isPreviewRoute ? "" : process.env.LINKEDIN_ADS_ACCOUNT_CREATION_CONVERSION_ID?.trim() ?? "",
    redditAdsPixelId: isPreviewRoute ? "" : process.env.REDDIT_ADS_PIXEL_ID?.trim() ?? "",
    cookieRegionCode:
      isPreviewRoute ? undefined : request.headers.get("oai-subdivision-1-iso-code") ?? undefined,
    theme,
    locale,
    messages,
  });
}

export function shouldRevalidate({ currentUrl, nextUrl }: { currentUrl: URL; nextUrl: URL }) {
  if (
    hasCreateNewAdAccountIntent(nextUrl.searchParams) &&
    (currentUrl.pathname !== nextUrl.pathname || currentUrl.search !== nextUrl.search)
  ) {
    return true;
  }

  // Pending-invitation activation compares refreshed access against the complete
  // bootstrap account set, while plain onboarding needs that set plus full
  // create-account eligibility. Revalidate even when act is unchanged so a
  // selected-only Ads Manager route cannot carry its narrowed user here.
  if (
    (isPendingInvitationsPath(nextUrl.pathname) || isNoAdAccountPath(nextUrl.pathname)) &&
    currentUrl.pathname !== nextUrl.pathname
  ) {
    return true;
  }

  if (
    getEffectiveLandingLocale(currentUrl.pathname) !== getEffectiveLandingLocale(nextUrl.pathname)
  ) {
    return true;
  }

  if (
    currentUrl.searchParams.get(LOCALE_SEARCH_PARAM) !==
    nextUrl.searchParams.get(LOCALE_SEARCH_PARAM)
  ) {
    return true;
  }

  const currentAdAccountId = getAdAccountIdFromSearchParams(currentUrl.searchParams);
  const nextAdAccountId = getAdAccountIdFromSearchParams(nextUrl.searchParams);

  if (currentAdAccountId !== nextAdAccountId) {
    return true;
  }

  return !isPublicPath(nextUrl.pathname) && !nextAdAccountId;
}

function SubRoot({
  statsigBootstrap,
  adsMeasurementPixelId,
  adsMeasurementDebug,
  googleAdsTagId,
  googleAdsAccountCreationLabel,
  metaAdsPixelId,
  linkedInAdsPartnerId,
  linkedInAdsAccountCreationConversionId,
  redditAdsPixelId,
  cookieRegionCode,
  cookieConsentBootstrap,
  locale,
  messages,
}: {
  statsigBootstrap: StatsigBootstrapData;
  adsMeasurementPixelId: string;
  adsMeasurementDebug: boolean;
  googleAdsTagId: string;
  googleAdsAccountCreationLabel: string;
  metaAdsPixelId: string;
  linkedInAdsPartnerId: string;
  linkedInAdsAccountCreationConversionId: string;
  redditAdsPixelId: string;
  cookieRegionCode?: string;
  cookieConsentBootstrap: CookieConsentBootstrapProps;
  locale: RootLoaderData["locale"];
  messages: RootLoaderData["messages"];
}) {
  const revalidator = useRevalidator();
  const { adsManagerStatsigClient, isStatsigBootstrapCurrent, statsigClient } = useStatsigClient(
    statsigBootstrap,
    revalidator.revalidate,
  );
  const location = useLocation();
  const searchParams = new URLSearchParams(location.search);
  const cookieCountryOverride = getDevCookieCountryOverride(searchParams);
  const cookieRegionOverride = getDevCookieRegionOverride(searchParams);
  const cookieCountryCode = cookieCountryOverride ?? statsigBootstrap.user.country;
  const resolvedCookieRegionCode = cookieRegionOverride ?? cookieRegionCode;

  return (
    <StatsigProvider client={statsigClient}>
      <StatsigGatedCookieConsentProvider {...cookieConsentBootstrap}>
        {/* This hook consumes cookie consent and must render below its provider. */}
        <StatsigSessionReplayClient
          adsManagerStatsigClient={adsManagerStatsigClient}
          user={statsigBootstrap.user}
        />
        <AdsMeasurementPixel pixelId={adsMeasurementPixelId} debug={adsMeasurementDebug} />
        <GoogleAdsTag
          tagId={googleAdsTagId}
          accountCreationLabel={googleAdsAccountCreationLabel}
          countryCode={cookieCountryCode}
        />
        <MetaAdsTag pixelId={metaAdsPixelId} countryCode={cookieCountryCode} />
        <LinkedInAdsTag
          partnerId={linkedInAdsPartnerId}
          accountCreationConversionId={linkedInAdsAccountCreationConversionId}
          countryCode={cookieCountryCode}
        />
        <RedditAdsTag pixelId={redditAdsPixelId} countryCode={cookieCountryCode} />
        <ClientTelemetryProvider>
          <StaleNavigationProvider>
            <ClientFirstPaintLogger />
            <ClientRouteLoadLogger />
            <PlatformUIKitProvider linkComponent={Link}>
              <IntlProvider locale={locale} messages={messages}>
                <CookieLocationProvider
                  countryCode={cookieCountryCode}
                  regionCode={resolvedCookieRegionCode}
                >
                  <RouterNavigationProgressBar />
                  <StatsigBootstrapReadinessContext.Provider value={isStatsigBootstrapCurrent}>
                    <Outlet />
                  </StatsigBootstrapReadinessContext.Provider>
                  <CookieBanner countryCode={cookieCountryCode} />
                  <CookiePreferences />
                </CookieLocationProvider>
              </IntlProvider>
            </PlatformUIKitProvider>
          </StaleNavigationProvider>
        </ClientTelemetryProvider>
      </StatsigGatedCookieConsentProvider>
    </StatsigProvider>
  );
}

function StatsigSessionReplayClient({
  adsManagerStatsigClient,
  user,
}: {
  adsManagerStatsigClient: Parameters<typeof useStatsigSessionReplayClient>[0];
  user: Parameters<typeof useStatsigSessionReplayClient>[1];
}) {
  useStatsigSessionReplayClient(adsManagerStatsigClient, user);
  return null;
}

function StatsigGatedCookieConsentProvider({
  children,
  ...initialConsent
}: CookieConsentBootstrapProps & { children: React.ReactNode }) {
  const includePersonalizationConsent = useGateValue(COOKIE_PERSONALIZATION_CHOICES_ENABLED_GATE, {
    disableExposureLog: true,
  });

  return (
    <CookieConsentProvider
      {...initialConsent}
      includePersonalizationConsent={includePersonalizationConsent}
    >
      {children}
    </CookieConsentProvider>
  );
}

function PreviewRoot({
  locale,
  messages,
}: {
  locale: RootLoaderData["locale"];
  messages: RootLoaderData["messages"];
}) {
  return (
    <PlatformUIKitProvider linkComponent={Link}>
      <IntlProvider locale={locale} messages={messages}>
        <Outlet />
      </IntlProvider>
    </PlatformUIKitProvider>
  );
}

function AppRoot({
  loaderData: {
    user,
    adAccountId,
    adAccountStatus,
    adAccountCountryCode,
    adAccountCurrencyCode,
    adAccountTimezone,
    adAccountUrl,
    adAccountIsSalesManaged,
    adAccountAdvertiserManagementType,
    adAccountHasAdsOnboardingIntent,
    adAccountBrandReviewStatus,
    adAccountBrandReviewReason,
    adAccountIntegrityReviewStatus,
    adAccountNeedsPersonaVerification,
    adAccountNeedsPersonaVerificationStepUp,
    adAccountPersonaVerificationStatus,
    adAccountShouldBeOnHoldingScreenPostVerification,
    adAccountAdsManagerAccessHoldReason,
    adAccountOnboardingAvailabilityHoldReason,
    adAccountIntegrityReviewFailureReason,
    adAccountPersonaVerificationApprovedBannerDismissed,
    adAccountPermissions,
    canCreateNewAdAccountWithoutLimit,
    isCreateNewAdAccountEnabled,
    isMobileWebAccountSetupEnabled,
    shouldRunOnboardingCampaignSetup,
    adsInsightsV2ReadEnabled,
    statsigBootstrap,
    initialAnalyticsConsent,
    initialMarketingConsent,
    initialPersonalizationConsent,
    initialAnalyticsConsentSource,
    initialMarketingConsentSource,
    initialPersonalizationConsentSource,
    initialCookieBannerDismissed,
    adsMeasurementPixelId,
    adsMeasurementDebug,
    googleAdsTagId,
    googleAdsAccountCreationLabel,
    metaAdsPixelId,
    linkedInAdsPartnerId,
    linkedInAdsAccountCreationConversionId,
    redditAdsPixelId,
    cookieRegionCode,
    locale,
    messages,
  },
}: Route.ComponentProps) {
  const globalContext: Partial<GlobalContext> = useMemo(
    () => ({
      user,
      adAccountId,
      adAccountStatus,
      adAccountCountryCode,
      adAccountCurrencyCode,
      adAccountTimezone,
      adAccountUrl,
      adAccountIsSalesManaged,
      adAccountAdvertiserManagementType,
      adAccountHasAdsOnboardingIntent,
      adAccountBrandReviewStatus,
      adAccountBrandReviewReason,
      adAccountIntegrityReviewStatus,
      adAccountNeedsPersonaVerification,
      adAccountNeedsPersonaVerificationStepUp,
      adAccountPersonaVerificationStatus,
      adAccountShouldBeOnHoldingScreenPostVerification,
      adAccountAdsManagerAccessHoldReason,
      adAccountOnboardingAvailabilityHoldReason,
      adAccountIntegrityReviewFailureReason,
      adAccountPersonaVerificationApprovedBannerDismissed,
      adAccountPermissions,
      canCreateNewAdAccountWithoutLimit,
      isCreateNewAdAccountEnabled,
      isMobileWebAccountSetupEnabled,
      shouldRunOnboardingCampaignSetup,
      adsInsightsV2ReadEnabled,
    }),
    [
      user,
      adAccountId,
      adAccountStatus,
      adAccountCountryCode,
      adAccountCurrencyCode,
      adAccountTimezone,
      adAccountUrl,
      adAccountIsSalesManaged,
      adAccountAdvertiserManagementType,
      adAccountHasAdsOnboardingIntent,
      adAccountBrandReviewStatus,
      adAccountBrandReviewReason,
      adAccountIntegrityReviewStatus,
      adAccountNeedsPersonaVerification,
      adAccountNeedsPersonaVerificationStepUp,
      adAccountPersonaVerificationStatus,
      adAccountShouldBeOnHoldingScreenPostVerification,
      adAccountAdsManagerAccessHoldReason,
      adAccountOnboardingAvailabilityHoldReason,
      adAccountIntegrityReviewFailureReason,
      adAccountPersonaVerificationApprovedBannerDismissed,
      adAccountPermissions,
      canCreateNewAdAccountWithoutLimit,
      isCreateNewAdAccountEnabled,
      isMobileWebAccountSetupEnabled,
      shouldRunOnboardingCampaignSetup,
      adsInsightsV2ReadEnabled,
    ],
  );

  return (
    <GlobalContextProvider state={globalContext}>
      <SubRoot
        statsigBootstrap={statsigBootstrap}
        adsMeasurementPixelId={adsMeasurementPixelId}
        adsMeasurementDebug={adsMeasurementDebug}
        googleAdsTagId={googleAdsTagId}
        googleAdsAccountCreationLabel={googleAdsAccountCreationLabel}
        metaAdsPixelId={metaAdsPixelId}
        linkedInAdsPartnerId={linkedInAdsPartnerId}
        linkedInAdsAccountCreationConversionId={linkedInAdsAccountCreationConversionId}
        redditAdsPixelId={redditAdsPixelId}
        cookieRegionCode={cookieRegionCode}
        cookieConsentBootstrap={{
          initialAnalyticsConsent,
          initialMarketingConsent,
          initialPersonalizationConsent,
          initialAnalyticsConsentSource,
          initialMarketingConsentSource,
          initialPersonalizationConsentSource,
          initialCookieBannerDismissed,
        }}
        locale={locale}
        messages={messages}
      />
      <AppToaster />
      <ReactQueryDevtools initialIsOpen={false} buttonPosition="bottom-right" />
    </GlobalContextProvider>
  );
}

export default function Root(props: Route.ComponentProps) {
  const location = useLocation();

  if (isAdPreviewPath(location.pathname)) {
    return <PreviewRoot locale={props.loaderData.locale} messages={props.loaderData.messages} />;
  }

  return <AppRoot {...props} />;
}

// Last resort fallback error boundary for all routes (including errors in the root route component).
// Keep this component strictly static. It should not be possible to generate an error in this component.
export function ErrorBoundary({ error }: Route.ErrorBoundaryProps) {
  if (error && error instanceof Error) {
    console.error(error);
  }

  const isServiceUnavailable = isRouteErrorResponse(error) && error.status === 503;

  return (
    <IntlProvider locale={DEFAULT_LOCALE} messages={{}}>
      <div className="flex w-full grow flex-col items-center justify-center gap-6 px-4 pt-12 text-center">
        <h1 className="text-3xl font-semibold tracking-wide text-neutral-900 uppercase">
          <FormattedMessage
            {...(isServiceUnavailable ?
              rootMessages.serviceUnavailableTitle
            : rootMessages.genericErrorTitle)}
          />
        </h1>
        <p className="max-w-[560px] text-lg text-neutral-600">
          <FormattedMessage
            {...(isServiceUnavailable ?
              rootMessages.serviceUnavailableDescription
            : rootMessages.genericErrorDescription)}
          />
        </p>
        <Button
          variant="ghost"
          color="primary"
          onClick={() => location.reload()}
          className="text-lg"
        >
          <FormattedMessage {...rootMessages.reloadPageAction} />
        </Button>
      </div>
    </IntlProvider>
  );
}

export function links(): Route.LinkDescriptors {
  return [];
}

export function meta(): Route.MetaDescriptors {
  // Default metadata for Ads Manager console routes. The public landing page overrides this in
  // components/pages/landing.tsx with indexable marketing metadata.
  return [
    { title: "OpenAI Ads Manager" },
    { property: "og:site_name", content: "OpenAI Ads" },
    { property: "og:type", content: "website" },
    { property: "og:title", content: "OpenAI Ads Manager" },
    // TODO: Add launch-ready Open Graph and Twitter images once marketing provides them.
    { name: "twitter:card", content: "summary_large_image" },
    { name: "twitter:title", content: "OpenAI Ads Manager" },
    { name: "robots", content: "noindex,nofollow" },
  ];
}

function getEffectiveLandingLocale(pathname: string) {
  const normalizedPathname = normalizeRoutePathname(pathname);
  if (normalizedPathname === "/") {
    return DEFAULT_LOCALE;
  }

  return getLocaleFromLandingPathname(normalizedPathname);
}

function isNoAdAccountPath(pathname: string): boolean {
  return pathname === "/onboarding" || pathname === "/onboarding/";
}

function isOnboardingCampaignSetupPath(pathname: string): boolean {
  return (
    pathname === ONBOARDING_CAMPAIGN_SETUP_PATH || pathname === `${ONBOARDING_CAMPAIGN_SETUP_PATH}/`
  );
}

function isPendingInvitationsPath(pathname: string): boolean {
  return pathname === "/pending-invitations";
}

function getSalesLedOnboardingPrefillId(searchParams: URLSearchParams): string | null {
  const prefillId = searchParams.get("prefill_id")?.trim();
  return prefillId ? prefillId : null;
}

function getProspectCampaignTokenFromPath(pathname: string): string | null {
  const encodedToken = pathname.slice(1);
  if (
    !pathname.startsWith("/") ||
    pathname === "/" ||
    encodedToken.includes("/") ||
    !encodedToken.includes("%")
  ) {
    return null;
  }
  try {
    const token = decodeURIComponent(encodedToken);
    if (!token || token !== token.trim() || token.length < 32 || token.length > 4096) {
      return null;
    }
    return token;
  } catch {
    return null;
  }
}

function getOppref(searchParams: URLSearchParams): string | null {
  const oppref = searchParams.get(OPPREF_QUERY_PARAM)?.trim();
  return oppref || null;
}

function getLoginReturnToPath({
  url,
  salesLedOnboardingPrefillId,
  googleAdsClickId,
  prospectCampaignToken,
  metaAdsClickId,
  linkedInAdsClickId,
  redditAdsClickId,
}: {
  url: URL;
  salesLedOnboardingPrefillId: string | null;
  googleAdsClickId: GoogleAdsClickId | undefined;
  prospectCampaignToken: string | null;
  metaAdsClickId: string | undefined;
  linkedInAdsClickId: string | undefined;
  redditAdsClickId: string | undefined;
}): string | null {
  if (
    googleAdsClickId != null ||
    metaAdsClickId != null ||
    linkedInAdsClickId != null ||
    redditAdsClickId != null ||
    salesLedOnboardingPrefillId != null ||
    prospectCampaignToken != null ||
    getOppref(url.searchParams) != null ||
    hasCreateNewAdAccountIntent(url.searchParams) ||
    ((isNoAdAccountPath(url.pathname) || isOnboardingCampaignSetupPath(url.pathname)) &&
      isOnboardingAccountSetupFlow(url.searchParams)) ||
    isAccountSetupDeeplink(url) ||
    isAdvertiserIdentityVerificationDeeplink(url) ||
    isEditCampaignDeeplinkUrl(url.pathname, url.search)
  ) {
    const returnToUrl = new URL(url);
    removeGoogleAdsClickIdsFromSearchParams(returnToUrl.searchParams);
    if (googleAdsClickId) {
      returnToUrl.searchParams.set(googleAdsClickId.type, googleAdsClickId.value);
    }
    returnToUrl.searchParams.delete(META_ADS_CLICK_ID_QUERY_PARAM);
    if (metaAdsClickId) {
      returnToUrl.searchParams.set(META_ADS_CLICK_ID_QUERY_PARAM, metaAdsClickId);
    }
    returnToUrl.searchParams.delete(LINKEDIN_ADS_CLICK_ID_QUERY_PARAM);
    if (linkedInAdsClickId) {
      returnToUrl.searchParams.set(LINKEDIN_ADS_CLICK_ID_QUERY_PARAM, linkedInAdsClickId);
    }
    returnToUrl.searchParams.delete(REDDIT_ADS_CLICK_ID_QUERY_PARAM);
    if (redditAdsClickId) {
      returnToUrl.searchParams.set(REDDIT_ADS_CLICK_ID_QUERY_PARAM, redditAdsClickId);
    }
    return `${returnToUrl.pathname}${returnToUrl.search}`;
  }

  return null;
}

function hasEffectiveSignedOutMarketingConsent(request: Request): boolean {
  const cookieHeader = request.headers.get("cookie") ?? "";
  return (
    request.headers.get("Sec-GPC") !== "1" &&
    getCookieConsentFromHeader(cookieHeader, "marketing_consent") === "agreed" &&
    getCookieConsentSourceFromHeader(cookieHeader, "marketing_consent_source") === "signed_out"
  );
}

function isAccountSetupDeeplink(url: URL): boolean {
  return (
    url.pathname === "/manage/campaigns" && url.searchParams.has(ACCOUNT_SETUP_DEEPLINK_QUERY_PARAM)
  );
}

function isAdvertiserIdentityVerificationDeeplink(url: URL): boolean {
  return (
    url.pathname === "/manage/campaigns" &&
    url.searchParams.has(ADVERTISER_IDENTITY_VERIFICATION_DEEPLINK_QUERY_PARAM)
  );
}

function buildLoginPath({ returnTo }: { returnTo: string | null }): string {
  if (returnTo == null) {
    return "/auth/login";
  }

  const searchParams = new URLSearchParams({ returnTo });
  return `/auth/login?${searchParams.toString()}`;
}
